import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const POPUP_SECTION_ID = 'cart-vendor-offer-popup';
const POPUP_DISMISSED_KEY = 'cart-vendor-offer-popup-dismissed';

// Gives other dialogs, such as Quick Add, time to close after the add completes.
const POPUP_DELAY = 400;

/**
 * A custom element that adds a suggested product to the cart from the cart itself or from the pop-up.
 * The surrounding cart-items-component renders the updated section from the event promise.
 */
class CartVendorOfferComponent extends Component {
  /** @type {boolean} */
  #adding = false;

  /**
   * Adds the variant of the clicked suggestion to the cart.
   * @param {Event} event - The click event from a suggestion button.
   */
  add = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[data-variant-id]') : null;
    if (!(button instanceof HTMLButtonElement) || this.#adding) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.#adding = true;
    button.disabled = true;

    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    }

    const deferredPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredPromise.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sectionIds.join(','),
          }),
        })
      );
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message || 'Add to cart failed');

      const cart = await (await fetch(`${Theme.routes.cart_url}.json`)).json();
      deferredPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-vendor-offer',
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });

      // The cart re-renders and drops its card; the pop-up keeps it, so mark it as added.
      if (button.closest('.cart-vendor-offer-popup')) {
        button.removeAttribute('aria-label');
        button.textContent = Theme.translations.added;
      }
    } catch (error) {
      deferredPromise.reject(error);
      button.disabled = false;
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Add to cart failed',
          code: 'INVALID',
        })
      );
    } finally {
      this.#adding = false;
    }
  };
}

if (!customElements.get('cart-vendor-offer-component')) {
  customElements.define('cart-vendor-offer-component', CartVendorOfferComponent);
}

/** @type {HTMLDialogElement | null} */
let popup = null;
let showingPopup = false;

const isPopupDismissed = () => {
  try {
    return sessionStorage.getItem(POPUP_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

const dismissPopup = () => {
  try {
    sessionStorage.setItem(POPUP_DISMISSED_KEY, '1');
  } catch {
    // Storage can be blocked; the pop-up then only repeats on the next add.
  }
};

const resetPopupDismissal = () => {
  try {
    sessionStorage.removeItem(POPUP_DISMISSED_KEY);
  } catch {
    // Nothing to reset when storage is blocked.
  }
};

/** Creates the pop-up dialog once. Closing it, by button, Esc or backdrop, ends the offer for this visit. */
function getPopup() {
  if (popup?.isConnected) return popup;

  popup = document.createElement('dialog');
  popup.className = 'cart-vendor-offer-popup';
  popup.setAttribute('aria-labelledby', 'cart-vendor-offer-heading-popup');
  popup.addEventListener('click', (event) => {
    // The dialog has no padding, so a click on the dialog itself is a click on the backdrop.
    const onBackdrop = event.target === popup;
    const onClose = event.target instanceof Element && event.target.closest('[data-popup-close]');
    if (onBackdrop || onClose) popup?.close();
  });
  popup.addEventListener('close', () => {
    dismissPopup();
    popup?.replaceChildren();
  });
  document.body.append(popup);

  return popup;
}

/** Fetches the suggestions for the current cart and opens the pop-up when there is something to offer. */
async function showPopup() {
  if (showingPopup) return;
  showingPopup = true;

  try {
    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(`${root}?section_id=${POPUP_SECTION_ID}`, { cache: 'no-store' });
    if (!response.ok) return;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const content = doc.querySelector('.cart-vendor-offer-popup__content');
    if (!content?.querySelector('.cart-vendor-offer__item')) return;
    if (document.querySelector('dialog[open]')) return;

    const dialog = getPopup();
    dialog.replaceChildren(document.importNode(content, true));
    dialog.showModal();
  } catch (error) {
    console.error(error);
  } finally {
    showingPopup = false;
  }
}

/**
 * Opens the pop-up after a product is added to the cart.
 * @param {CartLinesUpdateEvent} event
 */
async function handleCartLinesUpdate(event) {
  if (!Theme.cartVendorOfferPopup || Theme.template.name === 'cart') return;
  if (event.action !== 'add') return;

  // Adds made from a suggestion must not open another pop-up.
  if (event.target instanceof Element && event.target.closest('cart-vendor-offer-component')) return;

  try {
    const result = await event.promise;
    if (result?.detail?.didError) return;

    // When everything in the cart was just added, the shopper started over: offer the discount again.
    const addedQuantity = event.lines.reduce((total, line) => total + line.quantity, 0);
    if (result?.cart?.totalQuantity === addedQuantity) resetPopupDismissal();
  } catch {
    return;
  }

  await new Promise((resolve) => setTimeout(resolve, POPUP_DELAY));
  if (isPopupDismissed()) return;

  await showPopup();
}

document.addEventListener(StandardEvents.cartLinesUpdate, handleCartLinesUpdate);
